import {
  IncomingLineAuditTrail,
  IncomingLineDetails,
} from '@/components/bank-incoming/IncomingLineDetails';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBankIncomingLine } from '@/hooks/react-query/useBankIncoming';
import type { BankIncomingAdminLine } from '@/types/bank-reconciliation';
import type { FundingStep } from '@/types/currency-conversion';
import { formatCurrency } from '@/utils/formatters';

const ROLE_DESCRIPTIONS: Record<FundingStep['role'], string> = {
  FUNDING_OUT:
    'Money on its way out, towards the account this trade was dealt on.',
  FUNDING_IN: 'Money arriving on the account this trade was dealt on.',
  DISPOSED: 'What the trade sold.',
  ACQUIRED: 'What the trade bought.',
};

export interface FundingStepDetailsProps {
  fundId: string;
  /** The row an operator clicked, or null while the dialog is closed. */
  step: FundingStep | null;
  onClose: () => void;
}

export function FundingStepDetails({
  fundId,
  step,
  onClose,
}: FundingStepDetailsProps) {
  return (
    <Dialog open={step !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {step && <StagedLine fundId={fundId} step={step} />}
      </DialogContent>
    </Dialog>
  );
}

// Read from the staging table rather than the chain, which carries only what
// its table shows.
function StagedLine({ fundId, step }: { fundId: string; step: FundingStep }) {
  const scope = { fundId };
  const { data: line, isLoading } = useBankIncomingLine(
    step.incoming_id,
    scope
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className="tabular-nums">
            {formatCurrency(step.amount, { currency: step.currency })}
          </span>
          <span className="text-muted-foreground">·</span>
          <span>{step.account_name}</span>
          {step.is_evidence && <Badge className="font-normal">Evidence</Badge>}
        </DialogTitle>
        <DialogDescription>{ROLE_DESCRIPTIONS[step.role]}</DialogDescription>
      </DialogHeader>

      <StagedLineBody isLoading={isLoading} line={line} />

      <IncomingLineAuditTrail incomingId={step.incoming_id} scope={scope} />
    </>
  );
}

function StagedLineBody({
  isLoading,
  line,
}: {
  isLoading: boolean;
  line: BankIncomingAdminLine | undefined;
}) {
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading the bank transaction…
      </p>
    );
  }

  if (!line) {
    return (
      <p className="text-sm text-muted-foreground">
        The bank transaction behind this line is no longer available.
      </p>
    );
  }

  return <IncomingLineDetails line={line} />;
}
